import React, { useMemo, useState, useEffect, useRef } from 'react';
import { 
  MapPin, 
  Clock, 
  Calendar, 
  ChevronRight, 
  ArrowUp, 
  ArrowDown, 
  Map as MapIcon, 
  Navigation,
  Navigation2,
  MoreVertical,
  Route as RouteIcon,
  Activity,
  User,
  ExternalLink,
  Loader2,
  Maximize2,
  Minimize2,
  Eye,
  Layers,
  Flag,
  AlertCircle,
  CheckCircle2,
  Ban,
  Timer
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Inspection, Location } from '../types';
import { supabase } from '../lib/supabase';

// Fix for default marker icons in Leaflet with ESM
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface RoutePlannerProps {
  inspections: Inspection[];
  setInspections: React.Dispatch<React.SetStateAction<Inspection[]>>;
  locations: Location[];
  onNavigateToInspection: (id: string) => void;
}

const RecenterMap = ({ points }: { points: [number, number][] }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [points, map]);
  return null;
};

const RoutePlanner: React.FC<RoutePlannerProps> = ({ inspections, setInspections, locations, onNavigateToInspection }) => {
  const [viewMode, setViewMode] = useState<'kanban' | 'split'>('kanban');
  const [activeDateKey, setActiveDateKey] = useState<string>('Today');
  const [coords, setCoords] = useState<Record<string, [number, number]>>({});
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ current: 0, total: 0 });

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const todayStr = now.toISOString().split('T')[0];
  
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const groupedInspections = useMemo(() => {
    const grouped: Record<string, Inspection[]> = {};
    // Include Visited and Skipped in the route planner for progress visualization
    const relevantInsps = inspections.filter(i => 
      i.status !== 'Cancelled' && 
      i.status !== 'Completed'
    );
    
    relevantInsps.forEach(insp => {
      const dateKey = insp.date === todayStr ? 'Today' : (insp.date === tomorrowStr ? 'Tomorrow' : insp.date);
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(insp);
    });

    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        if (a.routeIndex !== undefined && b.routeIndex !== undefined && a.routeIndex !== b.routeIndex) {
          return a.routeIndex - b.routeIndex;
        }
        return a.time.localeCompare(b.time);
      });
    });

    return grouped;
  }, [inspections, todayStr, tomorrowStr]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedInspections).sort((a, b) => {
      if (a === 'Today') return -1;
      if (b === 'Today') return 1;
      if (a === 'Tomorrow') return -1;
      if (b === 'Tomorrow') return 1;
      return a.localeCompare(b);
    });
  }, [groupedInspections]);

  // Robust geocoding logic
  useEffect(() => {
    const activeRoute = groupedInspections[activeDateKey] || [];
    if (activeRoute.length === 0) return;

    let isSubscribed = true;
    
    const geocodeQueue = async () => {
      const uniquePropIds = Array.from(new Set(activeRoute.map(i => i.propertyId)));
      const missingProperties = uniquePropIds
        .map(id => locations.find(l => l.id === id))
        .filter((l): l is Location => !!l && !coords[l.id]);

      if (missingProperties.length === 0) {
        setGeocoding(false);
        return;
      }

      setGeocoding(true);
      setGeocodeProgress({ current: 0, total: missingProperties.length });

      for (let i = 0; i < missingProperties.length; i++) {
        if (!isSubscribed) break;
        
        const prop = missingProperties[i];
        setGeocodeProgress(prev => ({ ...prev, current: i + 1 }));

        try {
          const query = `${prop.address}, ${prop.city}, ${prop.state}, ${prop.zip}`;
          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`, {
            headers: { 'Accept-Language': 'en-US,en;q=0.5' }
          });
          const data = await res.json();
          
          if (data && data[0]) {
            const lat = parseFloat(data[0].lat);
            const lon = parseFloat(data[0].lon);
            setCoords(prev => ({ ...prev, [prop.id]: [lat, lon] }));
          } else {
            const fallbackQuery = `${prop.city}, ${prop.state}`;
            const fRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackQuery)}&limit=1`);
            const fData = await fRes.json();
            if (fData && fData[0]) {
              setCoords(prev => ({ ...prev, [prop.id]: [parseFloat(fData[0].lat), parseFloat(fData[0].lon)] }));
            }
          }

          if (i < missingProperties.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1200));
          }
        } catch (e) {
          console.error(`Geocoding error for ${prop.address}:`, e);
        }
      }

      if (isSubscribed) setGeocoding(false);
    };

    geocodeQueue();
    
    return () => {
      isSubscribed = false;
    };
  }, [activeDateKey, groupedInspections, locations]);

  const updateSequence = async (dateKey: string, inspId: string, direction: 'up' | 'down') => {
    const column = [...groupedInspections[dateKey]];
    const index = column.findIndex(i => i.id === inspId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      [column[index - 1], column[index]] = [column[index], column[index - 1]];
    } else if (direction === 'down' && index < column.length - 1) {
      [column[index], column[index + 1]] = [column[index + 1], column[index]];
    } else {
      return;
    }

    const updatedWithIndices = column.map((insp, idx) => ({ ...insp, routeIndex: idx }));
    
    setInspections(prev => prev.map(p => {
      const updated = updatedWithIndices.find(u => u.id === p.id);
      return updated ? updated : p;
    }));

    try {
      const updates = updatedWithIndices.map(u => 
        supabase.from('inspections').update({ route_index: u.routeIndex }).eq('id', u.id)
      );
      await Promise.all(updates);
    } catch (err) {
      console.error("Route Persistence Failure:", err);
    }
  };

  const updateStatus = async (inspId: string, status: Inspection['status']) => {
    setInspections(prev => prev.map(i => i.id === inspId ? { ...i, status } : i));
    try {
      await supabase.from('inspections').update({ status }).eq('id', inspId);
    } catch (err) {
      console.error("Status Sync Error", err);
    }
  };

  const activePoints = useMemo(() => {
    const route = groupedInspections[activeDateKey] || [];
    return route
      .map(i => coords[i.propertyId])
      .filter((p): p is [number, number] => p !== undefined);
  }, [activeDateKey, groupedInspections, coords]);

  const getProperty = (id: string) => locations.find(l => l.id === id);

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'Visited': return <CheckCircle2 size={12} className="text-emerald-500" />;
      case 'Skipped': return <Ban size={12} className="text-rose-500" />;
      case 'In Progress': return <Timer size={12} className="text-amber-500" />;
      default: return <Clock size={12} className="text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Visited': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'Skipped': return 'bg-rose-50 text-rose-700 border-rose-100';
      case 'In Progress': return 'bg-amber-50 text-amber-700 border-amber-100';
      default: return 'bg-blue-50 text-blue-700 border-blue-100';
    }
  };

  return (
    <div className="h-full flex flex-col space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-6 rounded-2xl border border-slate-200 shadow-sm gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-[#606c38] text-white rounded-2xl shadow-lg shadow-[#606c38]/10">
            <RouteIcon size={24} />
          </div>
          <div>
            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tighter leading-none">Logistics Studio</h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1.5 flex items-center gap-2">
              {geocoding ? (
                <span className="text-[#606c38] flex items-center gap-2">
                  <Loader2 size={10} className="animate-spin" /> 
                  Resolving Location {geocodeProgress.current} of {geocodeProgress.total}...
                </span>
              ) : 'Geographic Route Sequencing'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl w-full sm:w-auto">
          <button 
            onClick={() => setViewMode('kanban')}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${viewMode === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}
          >
            <Layers size={14} /> Board
          </button>
          <button 
            onClick={() => setViewMode('split')}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${viewMode === 'split' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}
          >
            <MapIcon size={14} /> Split Map
          </button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <div className="flex-1 flex gap-6 overflow-x-auto no-scrollbar pb-8 min-h-0">
          {sortedDates.map(dateKey => (
            <div key={dateKey} className="w-[380px] flex flex-col shrink-0">
              <div className="mb-4 flex items-center justify-between px-2">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${dateKey === 'Today' ? 'bg-[#606c38] animate-pulse' : 'bg-slate-300'}`} />
                  <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest">{dateKey}</h4>
                </div>
                <span className="text-[10px] font-bold text-slate-400 bg-white border border-slate-200 px-2.5 py-0.5 rounded-full">{groupedInspections[dateKey].length} Tasks</span>
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar space-y-3 bg-slate-100/30 p-2 rounded-3xl border border-dashed border-slate-200">
                {groupedInspections[dateKey].map((insp, idx) => {
                  const prop = getProperty(insp.propertyId);
                  return (
                    <div key={insp.id} className={`group relative bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-xl hover:border-[#606c38]/30 transition-all duration-300 ${insp.status === 'Skipped' ? 'opacity-60 grayscale' : ''}`}>
                      <div className="p-5 space-y-4">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shadow-lg ${insp.status === 'Visited' ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white'}`}>
                              {idx + 1}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] font-black text-[#606c38] uppercase tracking-widest flex items-center gap-1.5 mb-1">
                                <Clock size={10} /> {insp.time}
                              </p>
                              <h5 className="font-bold text-slate-800 text-sm leading-tight truncate max-w-[200px]">{prop?.address}</h5>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => updateSequence(dateKey, insp.id, 'up')}
                              disabled={idx === 0}
                              className="p-1.5 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-[#606c38] disabled:opacity-20"
                            >
                              <ArrowUp size={14} />
                            </button>
                            <button 
                              onClick={() => updateSequence(dateKey, insp.id, 'down')}
                              disabled={idx === groupedInspections[dateKey].length - 1}
                              className="p-1.5 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-[#606c38] disabled:opacity-20"
                            >
                              <ArrowDown size={14} />
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 py-2">
                           {(['Scheduled', 'In Progress', 'Visited', 'Skipped'] as const).map(status => (
                              <button 
                                key={status}
                                onClick={() => updateStatus(insp.id, status)}
                                className={`px-2.5 py-1 rounded-md text-[8px] font-black uppercase tracking-tighter border transition-all ${insp.status === status ? getStatusColor(status) : 'bg-white text-slate-400 border-slate-100 hover:bg-slate-50'}`}
                              >
                                {status}
                              </button>
                           ))}
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-50">
                          <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 truncate uppercase tracking-tighter">
                            <MapPin size={12} className="text-slate-300" /> {prop?.city}, {prop?.zip}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 truncate uppercase tracking-tighter">
                            {getStatusIcon(insp.status)} {insp.status}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button 
                            onClick={() => onNavigateToInspection(insp.id)}
                            className="flex-1 py-2.5 bg-slate-50 hover:bg-slate-900 hover:text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
                          >
                            View Session
                          </button>
                          <button 
                            onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(prop?.address + ', ' + prop?.city)}`, '_blank')}
                            className="px-4 py-2.5 bg-[#606c38]/5 text-[#606c38] hover:bg-[#606c38] hover:text-white rounded-xl transition-all"
                          >
                            <Navigation size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0">
          <div className="w-full lg:w-[400px] flex flex-col shrink-0 h-[300px] lg:h-full">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col h-full overflow-hidden">
               <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <select 
                    className="bg-transparent border-none text-[11px] font-black text-slate-900 uppercase tracking-widest outline-none cursor-pointer"
                    value={activeDateKey}
                    onChange={(e) => setActiveDateKey(e.target.value)}
                  >
                    {sortedDates.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Active Sequence</span>
               </div>
               <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
                  {(groupedInspections[activeDateKey] || []).map((insp, idx) => {
                    const prop = getProperty(insp.propertyId);
                    const isGeocoded = !!coords[insp.propertyId];
                    return (
                      <div key={insp.id} className={`p-4 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between group hover:bg-white hover:border-[#606c38]/30 transition-all cursor-pointer shadow-sm ${insp.status === 'Skipped' ? 'opacity-60 grayscale' : ''}`} onClick={() => onNavigateToInspection(insp.id)}>
                         <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-[10px] shrink-0 relative ${insp.status === 'Visited' ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white'}`}>
                              {idx+1}
                              {!isGeocoded && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 border-2 border-white rounded-full animate-pulse" />}
                            </div>
                            <div className="min-w-0">
                               <p className="text-xs font-bold text-slate-800 truncate uppercase tracking-tight">{prop?.address}</p>
                               <p className="text-[9px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
                                 {getStatusIcon(insp.status)} {insp.time} 
                                 {!isGeocoded && <span className="text-amber-500 font-black ml-1">• Resolving...</span>}
                               </p>
                            </div>
                         </div>
                         <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={(e) => { e.stopPropagation(); updateSequence(activeDateKey, insp.id, 'up'); }} className="p-1 text-slate-400 hover:text-[#606c38]"><ArrowUp size={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); updateSequence(activeDateKey, insp.id, 'down'); }} className="p-1 text-slate-400 hover:text-[#606c38]"><ArrowDown size={14} /></button>
                         </div>
                      </div>
                    );
                  })}
               </div>
            </div>
          </div>

          <div className="flex-1 min-h-[400px] lg:h-full bg-slate-200 rounded-[2rem] relative overflow-hidden shadow-inner border-2 border-white">
            <MapContainer 
              center={[37.0902, -95.7129]} 
              zoom={4} 
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <RecenterMap points={activePoints} />
              {(groupedInspections[activeDateKey] || []).map((insp, idx) => {
                 const pos = coords[insp.propertyId];
                 if (!pos) return null;
                 const prop = getProperty(insp.propertyId);
                 
                 const markerIcon = L.divIcon({
                    className: `custom-marker ${insp.status === 'Visited' ? 'marker-visited' : insp.status === 'Skipped' ? 'marker-skipped' : ''}`,
                    html: `<span>${idx + 1}</span>`,
                    iconSize: [28, 28],
                    iconAnchor: [14, 14],
                    popupAnchor: [0, -14]
                 });

                 return (
                   <Marker key={insp.id} position={pos} icon={markerIcon}>
                      <Popup>
                         <div className="p-2 min-w-[150px]">
                            <p className="text-[10px] font-black text-[#606c38] uppercase tracking-widest mb-1">{idx+1} • {insp.time}</p>
                            <h4 className="font-bold text-slate-900 text-sm mb-2">{prop?.address}</h4>
                            <div className={`mb-4 px-2 py-1 rounded text-[8px] font-black uppercase inline-block border ${getStatusColor(insp.status)}`}>{insp.status}</div>
                            <div className="grid grid-cols-1 gap-2">
                               <button 
                                 onClick={() => onNavigateToInspection(insp.id)}
                                 className="w-full py-1.5 bg-slate-900 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-800"
                               >
                                 Enter Session
                               </button>
                               <div className="flex gap-1 mt-1">
                                  <button onClick={() => updateStatus(insp.id, 'Visited')} className="flex-1 py-1 bg-emerald-500 text-white text-[8px] font-black uppercase rounded">Visit</button>
                                  <button onClick={() => updateStatus(insp.id, 'Skipped')} className="flex-1 py-1 bg-rose-500 text-white text-[8px] font-black uppercase rounded">Skip</button>
                               </div>
                            </div>
                         </div>
                      </Popup>
                   </Marker>
                 );
              })}
            </MapContainer>
            
            {/* Map Loading State */}
            {geocoding && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white/90 backdrop-blur-md px-4 py-2 rounded-full border border-[#606c38]/20 shadow-xl flex items-center gap-3 animate-in slide-in-from-top-4">
                 <Loader2 size={12} className="animate-spin text-[#606c38]" />
                 <span className="text-[9px] font-black text-slate-700 uppercase tracking-widest">
                   Mapping Destinations: {geocodeProgress.current} / {geocodeProgress.total}
                 </span>
              </div>
            )}

            {/* Map Legends */}
            <div className="absolute bottom-6 left-6 z-[1000] bg-white/90 backdrop-blur-md p-4 rounded-2xl border border-slate-200 shadow-2xl space-y-3">
               <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm" />
                  <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Visited / Completed</span>
               </div>
               <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500 shadow-sm" />
                  <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Scheduled Stop</span>
               </div>
               <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-rose-500 shadow-sm opacity-60" />
                  <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Skipped Stop</span>
               </div>
               <p className="text-[8px] text-slate-400 font-bold uppercase pt-2 border-t border-slate-100">Dynamic Logistics Engine</p>
            </div>
          </div>
        </div>
      )}

      {sortedDates.length === 0 && (
        <div className="w-full flex flex-col items-center justify-center py-40 text-center space-y-6">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center border-4 border-slate-50 shadow-xl">
            <MapIcon size={40} className="text-slate-200" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-black text-slate-300 uppercase tracking-tighter">Route Registry Empty</h3>
            <p className="text-sm text-slate-400 font-bold uppercase tracking-widest">Awaiting scheduled field sessions</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutePlanner;